import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MinusIcon,
  PlusIcon,
  ChevronDownIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import { wilayas, getCommunes } from '../data/algeriaLocations';
import ProductCard from '../components/ProductCard';
import JeansSizeAnimation from '../components/JeansSizeAnimation';
import type { Product } from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '';

const getImageUrl = (path: string) => {
  if (path.startsWith('http')) return path;
  return `${API_BASE}${path}`;
};

const ProductDetails = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();

  const [product, setProduct] = useState<Product | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);

  // Delivery form fields
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [wilaya, setWilaya] = useState('');
  const [commune, setCommune] = useState('');
  const [address, setAddress] = useState('');
  const [submittingOrder, setSubmittingOrder] = useState(false);

  const [openAccordion, setOpenAccordion] = useState<string | null>('description');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const availableCommunes = getCommunes(wilaya);

  const lightboxPrev = useCallback(() => {
    if (!product) return;
    setSelectedImageIndex((i) => (i - 1 + product.images.length) % product.images.length);
  }, [product]);

  const lightboxNext = useCallback(() => {
    if (!product) return;
    setSelectedImageIndex((i) => (i + 1) % product.images.length);
  }, [product]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') lightboxPrev();
      else if (e.key === 'ArrowRight') lightboxNext();
      else if (e.key === 'Escape') setLightboxOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen, lightboxPrev, lightboxNext]);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/products/${productId}`);
        const data = await res.json();
        if (data.success) {
          const p = data.data;
          setProduct(p);
          // Meta Pixel — ViewContent fires when a product page is viewed
          if (window.fbq) {
            window.fbq('track', 'ViewContent', {
              content_ids: [p._id],
              content_name: p.name,
              content_type: 'product',
              value: p.salePrice || p.price,
              currency: 'DZD',
            });
          }
          if (p.sizes?.length) setSelectedSize(p.sizes[0]);
          if (p.colors?.length) setSelectedColor(p.colors[0]);
          if (p.category) {
            const relRes = await fetch(`/api/products?category=${p.category}&limit=4`);
            const relData = await relRes.json();
            if (relData.success) {
              setRelatedProducts(
                relData.data.products
                  .filter((rp: Product) => rp._id !== p._id && rp.isActive)
                  .slice(0, 4)
              );
            }
          }
        }
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
    setSelectedImageIndex(0);
    setQuantity(1);
    window.scrollTo(0, 0);
  }, [productId]);

  const handleTouchStart = (e: React.TouchEvent) => setTouchStart(e.touches[0].clientX);
  const handleTouchMove = (e: React.TouchEvent) => setTouchEnd(e.touches[0].clientX);
  const handleTouchEnd = () => {
    if (!product || product.images.length <= 1) return;
    const distance = touchStart - touchEnd;
    if (Math.abs(distance) > 50) {
      if (distance > 0 && selectedImageIndex < product.images.length - 1)
        setSelectedImageIndex((i) => i + 1);
      else if (distance < 0 && selectedImageIndex > 0)
        setSelectedImageIndex((i) => i - 1);
    }
  };

  const handleOrderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittingOrder(true);
    try {
      let formattedPhone = phoneNumber.replace(/\D/g, '');
      if (formattedPhone.startsWith('213')) formattedPhone = '+' + formattedPhone;
      else if (formattedPhone.startsWith('0')) formattedPhone = '+213' + formattedPhone.slice(1);
      else if (formattedPhone.length === 9 && /^[5-7]/.test(formattedPhone)) formattedPhone = '+213' + formattedPhone;
      else formattedPhone = '+213' + formattedPhone;

      if (!/^(\+213)[5-7][0-9]{8}$/.test(formattedPhone)) {
        alert('Please enter a valid Algerian phone number (e.g., 0555123456)');
        setSubmittingOrder(false);
        return;
      }

      const itemPrice = product?.salePrice || product?.price || 0;
      const subtotal = itemPrice * quantity;
      const deliveryFee = product?.freeDelivery ? 0 : (subtotal >= 8000 ? 0 : 500);
      const total = subtotal + deliveryFee;

      const response = await fetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer: { name: fullName, phone: formattedPhone, state: wilaya, city: commune, address },
          items: [{ product: product?._id, size: selectedSize, quantity, price: itemPrice }],
          subtotal,
          deliveryFee,
          total,
          notes: `Size: ${selectedSize}${selectedColor ? `, Color: ${selectedColor}` : ''}`,
        }),
      });
      const responseData = await response.json();

      if (response.ok && responseData.success) {
        // Meta Pixel — Purchase fires on completed checkout with real order value
        if (window.fbq && product) {
          window.fbq('track', 'Purchase', {
            value: total,
            currency: 'DZD',
            content_ids: [product._id],
            content_name: product.name,
            content_type: 'product',
            num_items: quantity,
          });
        }
        const orderNumber = responseData.data?.orderNumber || '';
        navigate(`/order-success?order=${orderNumber}&product=${encodeURIComponent(product?.name || '')}`);
      } else {
        if (responseData.errors && Array.isArray(responseData.errors)) {
          throw new Error(responseData.errors.map((err: { msg: string }) => err.msg).join('\n'));
        }
        throw new Error(responseData.message || 'Failed to submit order');
      }
    } catch (error: unknown) {
      alert('Error: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setSubmittingOrder(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-offwhite flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-offwhite flex items-center justify-center">
        <div className="text-center">
          <p className="font-display text-display-sm text-warmgray-400 mb-4">Product Not Found</p>
          <Link to="/collections" className="btn-primary">Browse Collections</Link>
        </div>
      </div>
    );
  }

  const currentPrice = product.salePrice || product.price;
  const savings = product.salePrice ? product.price - product.salePrice : 0;
  const discountPercent = product.salePrice
    ? Math.round(((product.price - product.salePrice) / product.price) * 100)
    : 0;
  const deliveryFee = product.freeDelivery ? 0 : (currentPrice * quantity >= 8000 ? 0 : 500);
  const orderTotal = currentPrice * quantity + deliveryFee;

  return (
    <>
      <div className="min-h-screen bg-offwhite pb-8">
        {/* Breadcrumb */}
        <div className="pt-24 md:pt-28">
          <div className="section-wide py-4">
            <nav className="flex items-center gap-2 text-xs tracking-wide text-warmgray-500">
              <Link to="/" className="hover:text-offblack transition-colors">Home</Link>
              <span>/</span>
              <Link to="/collections" className="hover:text-offblack transition-colors">Shop</Link>
              <span>/</span>
              <span className="text-offblack">{product.name}</span>
            </nav>
          </div>
        </div>

        {/* Product Section */}
        <section className="pb-16 md:pb-24">
          <div className="section-wide">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">

              {/* Image Gallery */}
              <div className="space-y-3">
                <div
                  className="relative aspect-[3/4] bg-warmgray-100 overflow-hidden cursor-zoom-in"
                  onClick={() => setLightboxOpen(true)}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <img
                    src={getImageUrl(product.images[selectedImageIndex] || product.images[0])}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-500"
                  />
                  {product.salePrice && product.salePrice > 0 && (
                    <span className="absolute top-4 left-4 badge-sale">-{discountPercent}%</span>
                  )}
                  {product.images.length > 1 && (
                    <>
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedImageIndex((i) => (i - 1 + product.images.length) % product.images.length); }}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white flex items-center justify-center transition-colors"
                        aria-label="Previous image"
                      >
                        <ChevronLeftIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedImageIndex((i) => (i + 1) % product.images.length); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white flex items-center justify-center transition-colors"
                        aria-label="Next image"
                      >
                        <ChevronRightIcon className="w-5 h-5" />
                      </button>
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 md:hidden">
                        {product.images.map((_, idx) => (
                          <span key={idx} className={`block w-1.5 h-1.5 rounded-full transition-colors ${idx === selectedImageIndex ? 'bg-offblack' : 'bg-offblack/30'}`} />
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {product.images.length > 1 && (
                  <div className="hidden md:flex gap-2 overflow-x-auto">
                    {product.images.map((img, idx) => (
                      <button key={idx} onClick={() => setSelectedImageIndex(idx)}
                        className={`shrink-0 w-20 h-24 bg-warmgray-100 overflow-hidden border-2 transition-colors ${selectedImageIndex === idx ? 'border-offblack' : 'border-transparent hover:border-warmgray-300'}`}>
                        <img src={getImageUrl(img)} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Product Info + Inline Order Form */}
              <div className="lg:py-4">
                <div className="space-y-6">

                  {/* Brand & Name */}
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase text-warmgray-500 mb-2">{product.brand}</p>
                    <h1 className="font-display text-display-md tracking-tight text-offblack">{product.name}</h1>
                  </div>

                  {/* Price */}
                  <div className="flex items-baseline gap-3">
                    <span className="font-display text-2xl text-offblack">{currentPrice.toLocaleString()} DA</span>
                    {product.salePrice && product.salePrice > 0 && (
                      <>
                        <span className="text-warmgray-400 line-through text-lg">{product.price.toLocaleString()} DA</span>
                        <span className="text-xs tracking-wider uppercase text-sand font-medium">Save {savings.toLocaleString()} DA</span>
                      </>
                    )}
                  </div>

                  {product.freeDelivery && (
                    <div className="inline-flex items-center gap-3 bg-gradient-to-r from-green-600 to-emerald-500 text-white px-4 py-2 rounded-sm shadow-sm">
                      <TruckIcon className="w-4 h-4 shrink-0" />
                      <span className="text-xs font-semibold tracking-wide uppercase">Free Delivery</span>
                      <span className="w-px h-4 bg-white/30" />
                      <span className="text-xs font-semibold" dir="rtl">توصيل مجاني</span>
                    </div>
                  )}

                  <div className="divider" />

                  {/* Size */}
                  {product.sizes.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="text-sm font-medium text-offblack">Size</label>
                        <span className="text-xs text-warmgray-500">{selectedSize}</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {product.sizes.map((size) => (
                          <button key={size} onClick={() => setSelectedSize(size)}
                            className={`min-w-[48px] h-12 px-4 border text-sm font-medium transition-all ${selectedSize === size ? 'border-offblack bg-offblack text-offwhite' : 'border-warmgray-300 text-offblack hover:border-offblack'}`}>
                            {size}
                          </button>
                        ))}
                      </div>

                      {/* Size Guide — hidden for shoes */}
                      {product.category !== 'shoes' && (
                        <div className="mt-4 border border-warmgray-200 bg-warmgray-50">
                          <div className="px-4 pt-4 pb-2">
                            <p className="text-xs uppercase tracking-[0.15em] text-warmgray-500 font-medium">Size Guide — دليل المقاسات</p>
                          </div>
                          <div className="px-4 pb-2">
                            <JeansSizeAnimation />
                          </div>
                          <div className="px-4 pb-4">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-offblack text-offwhite">
                                  <th className="py-1.5 px-2 text-left font-semibold tracking-wide">Size</th>
                                  <th className="py-1.5 px-2 text-left font-semibold tracking-wide">Waist</th>
                                  <th className="py-1.5 px-2 text-right font-semibold tracking-wide" dir="rtl">المقاس</th>
                                </tr>
                              </thead>
                              <tbody>
                                {[
                                  { size: '31', cm: '38' },
                                  { size: '32', cm: '40' },
                                  { size: '34', cm: '42' },
                                  { size: '36', cm: '44' },
                                  { size: '38', cm: '46' },
                                  { size: '40', cm: '48' },
                                ].map((row, i) => (
                                  <tr key={row.size} className={`border-t border-warmgray-200 ${selectedSize === row.size ? 'bg-offblack text-offwhite font-semibold' : i % 2 === 0 ? 'bg-white' : 'bg-warmgray-50'}`}>
                                    <td className="py-1.5 px-2">{row.size}</td>
                                    <td className="py-1.5 px-2">{row.cm} cm</td>
                                    <td className="py-1.5 px-2 text-right" dir="rtl">{row.cm} سم</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Color */}
                  {product.colors.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="text-sm font-medium text-offblack">Color</label>
                        <span className="text-xs text-warmgray-500">{selectedColor}</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {product.colors.map((color) => (
                          <button key={color} onClick={() => setSelectedColor(color)}
                            className={`px-4 h-12 border text-sm font-medium transition-all ${selectedColor === color ? 'border-offblack bg-offblack text-offwhite' : 'border-warmgray-300 text-offblack hover:border-offblack'}`}>
                            {color}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Quantity */}
                  <div>
                    <label className="block text-sm font-medium text-offblack mb-3">Quantity</label>
                    <div className="inline-flex items-center border border-warmgray-300">
                      <button onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                        className="w-12 h-12 flex items-center justify-center hover:bg-warmgray-100 transition-colors" aria-label="Decrease quantity">
                        <MinusIcon className="w-4 h-4" />
                      </button>
                      <span className="w-12 h-12 flex items-center justify-center text-sm font-medium border-x border-warmgray-300">{quantity}</span>
                      <button onClick={() => setQuantity((q) => Math.min(product.stock || 10, q + 1))}
                        className="w-12 h-12 flex items-center justify-center hover:bg-warmgray-100 transition-colors" aria-label="Increase quantity">
                        <PlusIcon className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <span className={`w-2 h-2 rounded-full ${product.stock > 0 ? 'bg-olive' : 'bg-warmgray-400'}`} />
                      <span className="text-xs text-warmgray-500">{product.stock > 0 ? 'In stock' : 'Out of stock'}</span>
                    </div>
                  </div>

                  <div className="divider" />

                  {/* Inline Order Form */}
                  <form onSubmit={handleOrderSubmit} className="space-y-4">
                    <p className="text-xs uppercase tracking-[0.2em] text-warmgray-500 font-semibold">Delivery Information</p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-offblack mb-1.5">Full Name *</label>
                        <input type="text" required value={fullName} onChange={(e) => setFullName(e.target.value)}
                          className="w-full border border-warmgray-200 px-3 py-2.5 text-sm text-offblack focus:outline-none focus:border-offblack transition-colors placeholder:text-warmgray-300"
                          placeholder="Your full name" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-offblack mb-1.5">Phone Number *</label>
                        <input type="tel" required value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)}
                          className="w-full border border-warmgray-200 px-3 py-2.5 text-sm text-offblack focus:outline-none focus:border-offblack transition-colors placeholder:text-warmgray-300"
                          placeholder="0555 123 456" />
                        <p className="text-[0.65rem] text-warmgray-400 mt-1">05 / 06 / 07 ···</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-offblack mb-1.5">Wilaya *</label>
                        <select required value={wilaya} onChange={(e) => { setWilaya(e.target.value); setCommune(''); }}
                          className="w-full border border-warmgray-200 px-3 py-2.5 text-sm text-offblack focus:outline-none focus:border-offblack transition-colors bg-white">
                          <option value="">Select wilaya</option>
                          {wilayas.map((w) => <option key={w} value={w}>{w}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-offblack mb-1.5">Commune *</label>
                        <select required value={commune} onChange={(e) => setCommune(e.target.value)}
                          className="w-full border border-warmgray-200 px-3 py-2.5 text-sm text-offblack focus:outline-none focus:border-offblack transition-colors bg-white disabled:opacity-40"
                          disabled={!wilaya}>
                          <option value="">{wilaya ? 'Select commune' : '—'}</option>
                          {availableCommunes.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-offblack mb-1.5">Address *</label>
                      <textarea required value={address} onChange={(e) => setAddress(e.target.value)}
                        className="w-full border border-warmgray-200 px-3 py-2.5 text-sm text-offblack focus:outline-none focus:border-offblack transition-colors resize-none placeholder:text-warmgray-300"
                        rows={2} placeholder="Street, building, floor..." />
                    </div>

                    {/* Order Total */}
                    <div className="bg-offblack px-4 py-3 flex items-center justify-between">
                      <div>
                        <p className="text-[0.6rem] uppercase tracking-widest text-warmgray-400">Order Total</p>
                        <p className="text-xs text-warmgray-400 mt-0.5">
                          {quantity} item{quantity > 1 ? 's' : ''} · {deliveryFee === 0 ? 'Free delivery' : `+${deliveryFee} DA delivery`}
                        </p>
                      </div>
                      <p className="font-display text-2xl text-offwhite">
                        {orderTotal.toLocaleString()} <span className="text-sm text-warmgray-400">DA</span>
                      </p>
                    </div>

                    <button type="submit" disabled={submittingOrder || product.stock === 0}
                      className="w-full bg-offblack text-offwhite py-4 text-sm font-semibold tracking-[0.12em] uppercase hover:bg-charcoal transition-colors disabled:opacity-50">
                      {submittingOrder ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Placing Order...
                        </span>
                      ) : product.stock === 0 ? 'Out of Stock' : 'Order Now →'}
                    </button>
                  </form>

                  <div className="divider" />

                  {/* Accordions */}
                  <div className="divide-y divide-warmgray-200">
                    <div>
                      <button onClick={() => setOpenAccordion((p) => p === 'description' ? null : 'description')}
                        className="accordion-trigger w-full flex items-center justify-between py-4 text-sm font-medium text-offblack">
                        Description
                        <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${openAccordion === 'description' ? 'rotate-180' : ''}`} />
                      </button>
                      {openAccordion === 'description' && (
                        <div className="pb-4 text-sm text-warmgray-600 leading-relaxed">
                          {product.description || 'No description available.'}
                        </div>
                      )}
                    </div>
                    <div>
                      <button onClick={() => setOpenAccordion((p) => p === 'shipping' ? null : 'shipping')}
                        className="accordion-trigger w-full flex items-center justify-between py-4 text-sm font-medium text-offblack">
                        Shipping & Delivery
                        <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${openAccordion === 'shipping' ? 'rotate-180' : ''}`} />
                      </button>
                      {openAccordion === 'shipping' && (
                        <div className="pb-4 text-sm text-warmgray-600 leading-relaxed space-y-2">
                          <p>We deliver across all 58 wilayas in Algeria.</p>
                          <ul className="list-disc list-inside space-y-1">
                            <li>Delivery within 2–5 business days</li>
                            <li>Cash on delivery available</li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="py-16 md:py-24 border-t border-warmgray-200">
            <div className="section-wide">
              <h2 className="font-display text-display-sm tracking-tight text-offblack mb-10">Complete the Look</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {relatedProducts.map((rp) => (
                  <ProductCard key={rp._id} product={rp} />
                ))}
              </div>
            </div>
          </section>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && product && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-offblack/95" onClick={() => setLightboxOpen(false)}>
          <button className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 transition-colors"
            onClick={() => setLightboxOpen(false)} aria-label="Close lightbox">
            <XMarkIcon className="h-6 w-6 text-white" />
          </button>
          <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
            {selectedImageIndex + 1} / {product.images.length}
          </div>
          {product.images.length > 1 && (
            <>
              <button className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 transition-colors"
                onClick={(e) => { e.stopPropagation(); lightboxPrev(); }} aria-label="Previous image">
                <ChevronLeftIcon className="h-6 w-6 text-white" />
              </button>
              <button className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 transition-colors"
                onClick={(e) => { e.stopPropagation(); lightboxNext(); }} aria-label="Next image">
                <ChevronRightIcon className="h-6 w-6 text-white" />
              </button>
            </>
          )}
          <img src={getImageUrl(product.images[selectedImageIndex])} alt={product.name}
            className="max-h-[85vh] max-w-[90vw] object-contain" onClick={(e) => e.stopPropagation()} />
          {product.images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 overflow-x-auto max-w-[90vw] px-4"
              onClick={(e) => e.stopPropagation()}>
              {product.images.map((img, idx) => (
                <img key={idx} src={getImageUrl(img)} alt={`${product.name} ${idx + 1}`}
                  onClick={() => setSelectedImageIndex(idx)}
                  className={`h-14 w-14 object-cover shrink-0 cursor-pointer transition-all border-2 ${idx === selectedImageIndex ? 'border-white opacity-100' : 'border-transparent opacity-40 hover:opacity-80'}`} />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ProductDetails;
